import QRCodeLib from 'qrcode';

export interface QROptions {
  size?: number;
  fgColor?: string;
  bgColor?: string;
  logoSize?: number; // percentage of QR size
  logoBorderWidth?: number;
  errorCorrectionLevel?: 'L' | 'M' | 'Q' | 'H'; // Added
}

// Clean image URL to prevent double prefixes
function cleanImageUrl(url: string): string {
  if (!url) return url;
  // Clean double prefixes
  let cleaned = url;
  cleaned = cleaned.replace(/\/backend\/backend\//g, '/backend/');
  cleaned = cleaned.replace(/\/public\/public\//g, '/public/');
  return cleaned;
}

// Simple image loader
async function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous'; // Allow cross-origin images for canvas
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
    // Clean URL before setting src
    img.src = cleanImageUrl(src);
  });
}

// Composite QR code with profile image overlay
async function compositeQRWithImage(
  qrDataUrl: string,
  imageUrl: string,
  qrSize: number,
  logoSizePercent: number,
  borderWidth: number
): Promise<string> {
  // Calculate device pixel ratio for high-DPI rendering
  const devicePixelRatio = window.devicePixelRatio || 1;
  // Use minimum 2x scaling for better quality, or devicePixelRatio if higher
  const scaleFactor = Math.max(2, devicePixelRatio);
  
  // Internal canvas size (higher resolution for sharp rendering)
  const internalSize = qrSize * scaleFactor;
  
  const canvas = document.createElement('canvas');
  canvas.width = internalSize;
  canvas.height = internalSize;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas context not available');

  // Enable high-quality image smoothing
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  // Scale context to maintain visual size while using higher internal resolution
  ctx.scale(scaleFactor, scaleFactor);

  // Draw QR code at base size (will be rendered at higher resolution due to scale)
  const qrImage = await loadImage(qrDataUrl);
  ctx.drawImage(qrImage, 0, 0, qrSize, qrSize);

  // Logo size & position (calculated at base size)
  const logoSize = (qrSize * logoSizePercent) / 100;
  const logoX = (qrSize - logoSize) / 2;
  const logoY = (qrSize - logoSize) / 2;

  // --- IMPORTANT: Clear background behind logo (improves scanning) ---
  ctx.save();
  ctx.beginPath();
  ctx.arc(qrSize / 2, qrSize / 2, logoSize / 2 + borderWidth, 0, Math.PI * 2);
  ctx.clip();
  ctx.clearRect(logoX - borderWidth, logoY - borderWidth, logoSize + borderWidth * 2, logoSize + borderWidth * 2);
  ctx.restore();

  // White border for contrast
  ctx.fillStyle = '#FFFFFF';
  ctx.beginPath();
  ctx.arc(qrSize / 2, qrSize / 2, logoSize / 2 + borderWidth, 0, Math.PI * 2);
  ctx.fill();

  // Draw circular logo with high-quality rendering
  ctx.save();
  ctx.beginPath();
  ctx.arc(qrSize / 2, qrSize / 2, logoSize / 2, 0, Math.PI * 2);
  ctx.clip();

  const profileImage = await loadImage(imageUrl);
  // Draw profile image with high-quality smoothing
  ctx.drawImage(profileImage, logoX, logoY, logoSize, logoSize);
  ctx.restore();

  return canvas.toDataURL('image/png');
}

// Main QR generator
export async function generateQRWithProfile(
  profileUrl: string,
  profileImageUrl?: string,
  options: QROptions = {}
): Promise<string> {
  const {
    size = 400, // larger = sharper scan
    fgColor = '#000000',
    bgColor = '#FFFFFF',
    logoSize = 25, // smaller overlay for better readability
    logoBorderWidth = 4,
    errorCorrectionLevel = 'H', // high tolerance for central logo
  } = options;

  try {
    const qrDataUrl = await QRCodeLib.toDataURL(profileUrl, {
      width: size,
      margin: 4, // white border around
      color: { dark: fgColor, light: bgColor },
      errorCorrectionLevel, // crucial fix
    });

    if (!profileImageUrl) return qrDataUrl;

    // Clean profile image URL before using it
    const cleanedImageUrl = cleanImageUrl(profileImageUrl);

    // Combine QR + Profile image
    return await compositeQRWithImage(qrDataUrl, cleanedImageUrl, size, logoSize, logoBorderWidth);
  } catch (error) {
    console.error('Error generating QR with profile:', error);
    throw error;
  }
}
